import {
  Box,
  Button,
  ButtonBase,
  Grid,
  Icon,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import axios from 'axios';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { LiveContainer } from '../../LiveContainer';
import { useCoeolytics } from '@/js/shared/context/CoeolyticsHooks';
import { AuthContext } from '@/js/shared/context/AuthContext';
import { useEventEmitter } from '@byteclaw/use-event-emitter';
import trackResize from '@/js/shared/utils/trackResize';
import { VideoChatContext } from '@/js/shared/context/VideoChatContext';
import UserAvatar from '@/js/shared/components/ui/UserAvatar';
import clsx from 'clsx';
import RedButton from '@/js/shared/components/ui/RedButton';
import { ImageSize, getImage } from '../../../../../shared/utils/imageFormat';
import theme from '@/js/twilio/theme';
import { infoList } from '@/js/shared/utils/DataFormat';

// import { AuthContext } from '../../context/AuthContext';
// import { VideoChatContext } from '../../context/VideoChatContext';
// import trackResize from '../../utils/trackResize';
// import RedButton from '../ui/RedButton';
// import UserAvatar from '../ui/UserAvatar';

const CHAT_HEIGHT = 155;
const PUBLIC_ROOMS_MAX_PARTICIPANTS = 50;

const usePublicStyles = makeStyles(() => ({
  holder: {
    position: 'relative',
    width: '100%',
  },
  content: {
    backgroundColor: '#333',
    overflowY: 'auto',
    overflowX: 'hidden',
    color: 'rgba(255, 255, 255, 0.7)',
    flexGrow: 1,
  },
  area: {
    width: '100%',
    // cursor: 'pointer',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    // display: 'block',
    // textAlign: 'left',
    backgroundColor: '#000',
    borderRadius: 5,
    minHeight: 100,
    padding: 0,
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  privateArea: {
    backgroundColor: '#000',
    borderRadius: 5,
    minHeight: 40,
    padding: 8,
    position: 'relative',
    marginBottom: 3,
    marginRight: 3,
  },
  visitorHolder: {
    margin: 2,
    // display: 'inline-block',
    verticalAlign: 'top',
    fontSize: 9,
    textAlign: 'center',
    overflow: 'hidden',
    width: 60,
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.5))',
  },
  visitorHolderJoined: {
    opacity: 0.5,
    verticalAlign: 'top',
  },
  joinButton: {
    color: '#FFFFFF',
    // minWidth: 40,
    // padding: '2px 4px',
    // position: 'absolute',
    // top: 0,
    // right: 0,
    // backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  lobby: {
    marginTop: 10,
    backgroundColor: '#222',
    borderRadius: 5,
    padding: 5,
    // color: 'rgba(255, 255, 255, 0.7)',
    // '&::after': {
    //   display: 'block',
    //   flex: 1,
    //   content: '""',
    //   backgroundColor: '#666666',
    //   height: 1,
    //   margin: '-10px 1rem 0 1rem',
    // },
    minHeight: 90,
  },
  lobbyTitle: {
    display: 'block',
    width: 90,
    textAlign: 'center',
    margin: '-12px auto 0 auto',
    color: '#FFFFFF',
    backgroundColor: '#222',
    fontSize: '0.8rem',
    padding: '3px 10px',
    borderRadius: 3,
  },
  toggleButton: {
    borderRadius: '5px 5px 0 0',
    backgroundColor: '#333',
    textDecoration: 'underline',
    textTransform: 'none',
    marginRight: 5,
    lineHeight: '1rem',
    color: '#FFFFFF',
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
      backgroundColor: '#333',
      // textDecoration: 'underline',
    },
  },
  endIcon: {
    marginLeft: 0,
  },
  addIcon: {
    verticalAlign: 'bottom',
    fontSize: 19,
  },
}));

export const useTimer = ({ startAt = 0, onTimeOver, onTimeUpdate }) => {
  const STEPS_MS = 1000;
  const [running, setRunning] = useState(false);
  const [startTime, setStartTime] = useState(startAt);
  const [time, setTime] = useState(startAt);
  // const timer = useRef();

  const restart = (newTime) => {
    setStartTime(newTime);
    setTime(newTime);
    setRunning(true);
  };

  const stop = () => {
    setRunning(false);
  };

  useEffect(() => {
    if (time <= 0) {
      setRunning(false);
      onTimeOver && onTimeOver();
    } else {
      onTimeUpdate && onTimeUpdate(time);
    }
  }, [time]);

  useEffect(() => {
    let intervalId;
    if (running) {
      intervalId = setInterval(
        () => setTime((currentTime) => currentTime - STEPS_MS),
        STEPS_MS
      );
    }
    return () => clearInterval(intervalId);
  }, [running]);

  return { restart, time, startTime, running, stop };
};

const PublicRooms = React.memo(
  ({
    memberCount,
    largeVideo,
  }: {
    memberCount: number;
    largeVideo: boolean;
  }) => {
    const classes = usePublicStyles();
    const timer = useRef();
    const [open, setOpen] = useState(true);
    const [publicRooms, setPublicRooms] = useState([]);
    const [privateRooms, setPrivateRooms] = useState([]);
    const {
      videoChatId,
      joinPublicVideoChat,
      handleSendInvitation,
      createEmptyPrivateRoom,
    } = useContext(VideoChatContext);

    // let [rouletteDelay, setRouletteDelay] = useState(3000); //delay for poll

    const findRandomRoom = () => {
      let availableRooms = publicRooms
        .filter((room) => room.visitors.length > 0 && room.visitors.length < 5)
        .map((room) => ({
          videoChatId: room.roomId,
          visitors: room.visitors.length,
        }));
      availableRooms = availableRooms.concat(
        privateRooms
          .filter((room) => {
            return (
              room.visitors.length > 0 &&
              room.visitors.length < 5 &&
              room.roomId !== videoChatId
            );
          })
          .map((room) => ({
            videoChatId: room.roomId,
            visitors: room.visitors.length,
          }))
      );
      if (availableRooms.length > 0) {
        const singles = availableRooms.filter(
          (room) => room.visitors.length === 1
        ); // Prioritice single rooms first
        if (singles.length > 0) {
          joinPublicVideoChat(
            singles[Math.floor(Math.random() * singles.length)].videoChatId
          );
        } else {
          joinPublicVideoChat(
            availableRooms[Math.floor(Math.random() * availableRooms.length)]
              .videoChatId
          );
        }
        return true;
      } else {
        return false;
      }
    };
    let { restart, time, running, stop } = useTimer({
      onTimeOver: () => {
        const success = findRandomRoom();
        if (!success) {
          restartRandomTimer();
        }
      },
      // onTimeUpdate: (time) => {
      //   console.log('update', time);
      // },
    }); //delay for poll

    const restartRandomTimer = (extra = 0) => {
      restart(Math.floor(extra + 6 + Math.random() * 6) * 1000);
    };
    const stopTimer = () => {
      stop();
    };

    const toggleShowRooms = () => {
      setOpen(!open);
    };
    const reloadRooms = () => {
      try {
        axios
          .get(
            `/api/visitor/conferences/${window.CONFERENCE_ID}/publicvideorooms`
          )
          .then((response) => {
            setPublicRooms(response.data.publicRooms);
            setPrivateRooms(response.data.privateRooms);
          });
      } catch (e) {
        console.log('Sponsor error', e);
      }
    };
    useEffect(() => {
      reloadRooms();
      timer.current = setInterval(reloadRooms, open ? 5000 : 20000);
      return () => {
        clearInterval(timer.current);
      };
    }, [open]);

    useEffect(() => {
      if (window.SETTINGS.USE_LOBBY_RANDOM_MEETING) {
        if (
          memberCount === 1 &&
          open &&
          videoChatId &&
          !videoChatId.startsWith('private') &&
          !videoChatId.startsWith('public')
        ) {
          restartRandomTimer(10);
          // Start chat roulette (user is all alone)
        } else {
          // Stop chat roulette
          stopTimer();
        }
      } else {
        stopTimer();
      }
    }, [memberCount, open, videoChatId]);

    const joinRoom = (roomId: string) => {
      joinPublicVideoChat(roomId);
    };

    return (
      <Box className={classes.holder} display="flex" flexDirection="column">
        {open && (
          <Box p={1} className={classes.content}>
            {publicRooms.length > 0 && (
              <>
                <Grid container spacing={1} alignItems="flex-end">
                  {publicRooms.map((room) => (
                    <Grid item xs={12} sm={largeVideo ? 12 : 6} key={room.id}>
                      <Box display="flex" alignItems="center">
                        <Box flexGrow={1}>{room.title}</Box>
                        <Box>
                          {videoChatId === room.roomId ? (
                            <Button
                              startIcon={<Icon>logout</Icon>}
                              className={classes.joinButton}
                              size="small"
                              color="primary"
                              onClick={() => handleSendInvitation(null, true)}
                            >
                              Lobby
                            </Button>
                          ) : room.visitors.length <
                            PUBLIC_ROOMS_MAX_PARTICIPANTS ? (
                            <Button
                              startIcon={<Icon>login</Icon>}
                              className={classes.joinButton}
                              size="small"
                              color="primary"
                              onClick={() => joinRoom(room.roomId)}
                            >
                              {window.LANG.JOIN_ROOM}
                            </Button>
                          ) : (
                            <Box className={classes.joinButton} p={1}>
                              Full
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <ButtonBase
                        className={classes.area}
                        disabled={videoChatId === room.roomId}
                        onClick={() => joinRoom(room.roomId)}
                        style={{
                          minHeight: largeVideo ? 100 : 250,
                          ...(videoChatId === room.roomId
                            ? { border: '1px solid #fff' }
                            : null),
                          ...(room.picture
                            ? {
                                backgroundImage: `url(${getImage(
                                  room.picture,
                                  ImageSize.Medium,
                                  window.CONFERENCE_ID
                                )})`,
                              }
                            : null),
                        }}
                      >
                        {room.visitors.map((user) => (
                          <Tooltip
                            title={infoList([
                              user.full_name,
                              user.title,
                              user.company,
                              user.location_city,
                            ])}
                            placement="top"
                            key={user.id}
                          >
                            <Box
                              key={user.id}
                              display="inline"
                              className={classes.visitorHolder}
                            >
                              <UserAvatar size="medium2" user={user} />
                              {user.first_name}
                            </Box>
                          </Tooltip>
                        ))}
                      </ButtonBase>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            <Box className={classes.lobby} align="left">
              <ButtonBase
                className={classes.lobbyTitle}
                onClick={() => handleSendInvitation(null, true)}
              >
                LOBBY
              </ButtonBase>

              {/*publicRooms.length > 0 && privateRooms.length > 0 && (
              <Box className={classes.title} align="center" p={1}>
                Pick a public room or add from{' '}
                <Icon className={classes.addIcon}>people</Icon>PEOPLE
              </Box>
            )}
            {publicRooms.length === 0 && (
              <Box className={classes.title} align="center" p={1}>
                Now add someone from{' '}
                <Icon className={classes.addIcon}>people</Icon>PEOPLE
              </Box>
            )*/}
              {window.SETTINGS.USE_LOBBY_RANDOM_MEETING && running && (
                <Box align="center">
                  {window.LANG.RANDOM_ROOM} <b>{Math.floor(time / 1000)} s</b>{' '}
                  <IconButton
                    color="inherit"
                    onClick={createEmptyPrivateRoom}
                    size="small"
                  >
                    <Icon>stop_circle</Icon>
                  </IconButton>
                </Box>
              )}
              {privateRooms.map((room) => (
                <Box
                  className={classes.privateArea}
                  key={room.roomId}
                  display="inline-block"
                >
                  {room.visitors
                    .filter((user) => user !== null)
                    .map((user) =>
                      videoChatId === room.roomId ? (
                        <ButtonBase
                          key={user.id}
                          disabled={true}
                          className={clsx(
                            classes.visitorHolder,
                            classes.visitorHolderJoined
                          )}
                        >
                          <UserAvatar size="medium2" user={user} />
                          {user.first_name}
                        </ButtonBase>
                      ) : (
                        <Tooltip
                          title={infoList([
                            user.full_name,
                            user.title,
                            user.company,
                            user.location_city,
                          ])}
                          placement="top"
                          key={user.id}
                        >
                          <ButtonBase
                            disabled={room.visitors.length > 6}
                            onClick={() => joinPublicVideoChat(room.roomId)}
                            className={clsx(
                              classes.visitorHolder,
                              room.visitors.length > 6 &&
                                classes.visitorHolderJoined
                            )}
                          >
                            <UserAvatar size="medium2" user={user} />
                            {user.first_name}
                          </ButtonBase>
                        </Tooltip>
                      )
                    )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => prevProps.memberCount === nextProps.memberCount
);

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },

  roomsList: {
    flexBasis: 'auto',
    flexGrow: 1,
    display: 'flex',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      overflow: 'hidden',
    },
  },
  roomsListSmall: {
    flexBasis: 300,
    flexGrow: 0,
  },
  roomScroller: {
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  videoHolder: {
    // border: '1px solid rgba(150, 150, 150, 0.4)',
    // borderBottomSize: 0,
    backgroundColor: '#000000',
    position: 'relative',
    // padding: 5,
    // paddingBottom: 0,
    minWidth: 300,
    overflow: 'hidden',
    // borderRadius: 5,
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 300px',
    },
  },
  videoHolderSmall: {
    flexBasis: 320,
    flexGrow: 0,
  },
  smallButton: {
    padding: '2px 4px',
    marginBottom: 2,
    marginRight: 1,
  },
  selectRoom: {
    position: 'absolute',
    borderRadius: 5,
    top: '20%',
    left: 10,
    right: 10,
    backgroundColor: theme.palette.background.default,
    zIndex: 2,
    padding: 15,
    textAlign: 'center',
    fontSize: '1.8rem',
    filter: `drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.3))`,
  },
  embedHolder: {
    // borderRadius: 5,
    overflow: 'hidden',
    height: '100%',
    position: 'relative',
  },
  videoEmbed: {
    position: 'absolute',
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
  },
  memberList: {
    overflow: 'hidden',
  },
  leaveButton: {
    padding: '2px 0px',
    minWidth: 'auto',
  },
  avatarHolder: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(80, 80, 80, 0.3)',
    },
  },
  addText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '0.8rem',
  },
  roomInfo: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  buttonHolder: {
    backgroundColor: theme.palette.background.default,
    borderBottomLeftRadius: 5,
    borderBottomRightRadius: 5,
    paddingLeft: 10,
    paddingRight: 10,
  },
  // addIcon: {
  //   verticalAlign: 'bottom',
  //   fontSize: 19,
  // },
}));

const sizeCalc = () => {
  const winW =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;
  return Math.floor(winW * 0.8);
};

const VIDEO_WIDTH = 155; // From webrtc.scss

export const MingleLarge = (props) => {
  const {
    createEmptyRoom,
    videoData,
    videoChatId,
    handleSendInvitation,
    handleVideoChatClose,
  } = useContext(VideoChatContext);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const showProfileEvent = (e) => {
    eventEmitter.emit('profile', e.detail);
  };

  const classes = useStyles();
  const showChatRef = useRef();
  const iframeRefStore = useRef();

  const iframeRef = useCallback((node) => {
    if (iframeRefStore.current) {
      iframeRefStore.current.removeEventListener(
        'showprofile',
        showProfileEvent
      );
    }
    if (node !== null) {
      node.addEventListener('showprofile', showProfileEvent);
      iframeRefStore.current = node;
    }
  }, []);

  const { authState } = useContext(AuthContext);

  const eventEmitter = useEventEmitter();
  const memersList = videoData.members.filter(
    (m) => m.id !== authState.user.id
  );
  showChatRef.current = true;
  //   const maxWidth = Math.min(
  //     5 * VIDEO_WIDTH + 60,
  //     trackResize(showChatRef, sizeCalc)
  //   );
  //   const width = Math.min(
  //     maxWidth,
  //     Math.max(330, videoData.members.length * VIDEO_WIDTH + 60)
  //   );
  useEffect(() => {
    handleSendInvitation();
    return () => {
      handleVideoChatClose();
    };
  }, []);

  const largeVideo =
    memersList.length > 0 || (videoChatId && videoChatId.startsWith('public'));
  // console.log('largeVideo', largeVideo);
  // console.log('lala', memersList.length, videoChatId);

  return (
    <LiveContainer
      isActive={(to) => {
        console.log('to', to);
        return to === `/live/meet`;
      }}
    >
      <Box className={classes.container}>
        <Box
          className={clsx(classes.videoHolder, {
            [classes.videoHolderSmall]: !largeVideo,
          })}
        >
          {largeVideo && (
            <Box className={classes.roomInfo}>
              <Box className={classes.buttonHolder}>
                <Button
                  onClick={() => eventEmitter.emit('newthread', memersList)}
                  className={classes.smallButton}
                  size="small"
                  startIcon={<Icon>forum</Icon>}
                >
                  chat
                </Button>
                &nbsp;
                <Button
                  className={classes.leaveButton}
                  size="small"
                  color="primary"
                  onClick={createEmptyRoom}
                  startIcon={<Icon>logout</Icon>}
                >
                  Lobby
                </Button>
              </Box>
            </Box>
          )}
          <Box className={classes.embedHolder}>
            <iframe
              ref={iframeRef}
              className={classes.videoEmbed}
              frameBorder="0"
              //   src={`/webrtcembed2?roomId=${props.videoChatId}`}
              width="100%"
              height="100%"
              scrolling="no"
              allow="microphone; camera"
              src={`/webrtcembed2?roomId=${videoChatId}&justwatch=0&gallery=1&host=1&breakout=false`}
            ></iframe>
          </Box>
          {!largeVideo && (
            <Box className={classes.selectRoom}>
              {window.LANG.SELECT_ROOM}
              <br />
              <Icon fontSize="large">{smallScreen ? 'south' : 'east'}</Icon>
            </Box>
          )}
        </Box>
        <Box
          className={clsx(classes.roomsList, {
            [classes.roomsListSmall]: largeVideo && !smallScreen,
          })}
        >
          <PublicRooms
            memberCount={videoData.members.length}
            largeVideo={smallScreen || largeVideo}
            videoChatId={videoChatId}
          />
        </Box>
      </Box>
    </LiveContainer>
  );
};

MingleLarge.displayName = 'MingleLarge';
